import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useCallback,
  useState,
  type ReactNode,
} from 'react';
import { authApi } from '../services/api';
import type { PlatformEvent, EventFilter } from '../types/events';
import { matchesFilters, computeUnion, MAX_FILTERS } from '../utils/event-filter-match';
import { eventDebug } from '../utils/event-debug';

export type SubscribeFn = (
  filters: EventFilter[],
  handler: (event: PlatformEvent) => void,
) => () => void;

export type DisconnectReason =
  | 'pong_timeout'
  | 'visibility_probe_timeout'
  | 'network_offline'
  | 'send_error'
  | 'server_close'
  | 'token_failure'
  | 'unmount'
  | 'unknown';

export interface ConnectionDiagnostics {
  connectedSince: number | null;
  disconnectCount: number;
  lastDisconnectReason: DisconnectReason | null;
  lastDisconnectTime: number | null;
  reconnectAttempts: number;
}

const INITIAL_DIAGNOSTICS: ConnectionDiagnostics = {
  connectedSince: null,
  disconnectCount: 0,
  lastDisconnectReason: null,
  lastDisconnectTime: null,
  reconnectAttempts: 0,
};

interface PlatformEventContextValue {
  subscribe: SubscribeFn;
  isConnected: boolean;
  diagnostics: ConnectionDiagnostics;
}

export const PlatformEventContext = createContext<PlatformEventContextValue | null>(null);

const WS_URL = import.meta.env.VITE_WS_URL as string;

// Keepalive: ping every 5 minutes (well under API Gateway's 10-min idle timeout)
const PING_INTERVAL_MS = 5 * 60 * 1000;
// If no message received within 15s of a keepalive ping, consider connection dead
const PONG_TIMEOUT_MS = 15_000;
// More generous timeout after sleep/tab switch — server may need warm-up time
const VISIBILITY_PROBE_TIMEOUT_MS = 15_000;
// Skip visibility probe if a message was received within this window
const VISIBILITY_RECENT_MS = 30_000;

// Reconnection backoff
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30_000;
const RECONNECT_JITTER = 0.5;

interface Subscription {
  id: number;
  filters: EventFilter[];
  handler: (event: PlatformEvent) => void;
}

const SEEN_IDS_MAX = 1000;
const SEEN_IDS_TRIM = 500;

export function PlatformEventProvider({ children }: { children: ReactNode }) {
  const [isConnected, setIsConnected] = useState(false);
  const [diagnostics, setDiagnostics] = useState<ConnectionDiagnostics>(INITIAL_DIAGNOSTICS);
  const wsRef = useRef<WebSocket | null>(null);
  const subscriptionsRef = useRef<Subscription[]>([]);
  const nextIdRef = useRef(1);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const mountedRef = useRef(true);
  const seenIdsRef = useRef(new Set<string>());
  const connectingRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval>>();
  const pongTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const lastMessageTimeRef = useRef(0);
  const disconnectReasonRef = useRef<DisconnectReason>('unknown');
  const connectedSinceRef = useRef<number | null>(null);
  const flushScheduledRef = useRef(false);
  const lastSentRef = useRef<string | null>(null);

  /**
   * Recompute the deduped union of all active subscriptions and send ONE
   * `{action:'set'}` (replace semantics). Skips redundant sends (byte-identical
   * to the last). No-op until the socket is OPEN — `onopen` calls this to send
   * the full set once connected.
   */
  const flushSet = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      // Cause #2 probe: a filter change tried to flush while the socket was
      // not OPEN, so the server's filter set is now stale until reconnect.
      eventDebug.recordFlush(computeUnion(subscriptionsRef.current), ws?.readyState ?? null);
      return;
    }

    let filters = computeUnion(subscriptionsRef.current);
    if (filters.length > MAX_FILTERS) {
      if (import.meta.env.DEV) {
        console.warn(
          `[WS] Filter union of ${filters.length} exceeds server cap of ` +
          `${MAX_FILTERS}; truncating. Consolidate overlapping filters.`,
        );
      }
      filters = filters.slice(0, MAX_FILTERS);
    }

    const serialized = JSON.stringify(filters);
    if (serialized === lastSentRef.current) return;
    try {
      ws.send(JSON.stringify({ action: 'set', filters }));
      lastSentRef.current = serialized;
      eventDebug.recordFlush(filters, ws.readyState);
    } catch {
      disconnectReasonRef.current = 'send_error';
      ws.close();
    }
  }, []);

  /**
   * Coalesce a `flushSet` to a microtask so a whole commit's worth of
   * mount/unmount churn (a route change subscribing/unsubscribing many hooks)
   * collapses into a single `set` message. Uses a boolean flag rather than a
   * timer id: a timer id left non-undefined after `clearTimeout` (e.g. across a
   * StrictMode remount that reuses refs) would permanently jam the scheduler,
   * freezing the server's filter set at whatever subscribed before connect.
   */
  const scheduleSync = useCallback(() => {
    if (flushScheduledRef.current) return;
    flushScheduledRef.current = true;
    queueMicrotask(() => {
      flushScheduledRef.current = false;
      flushSet();
    });
  }, [flushSet]);

  const scheduleReconnect = useCallback((connectFn: () => void) => {
    if (!mountedRef.current) return;
    const attempt = reconnectAttemptsRef.current++;
    const exponential = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS);
    const jitter = exponential * RECONNECT_JITTER * (Math.random() * 2 - 1);
    const delay = Math.max(0, exponential + jitter);
    console.debug(`[WS] Scheduling reconnect in ${Math.round(delay)}ms (attempt ${attempt})`);
    setDiagnostics((prev) => ({ ...prev, reconnectAttempts: attempt }));
    reconnectTimerRef.current = setTimeout(connectFn, delay);
  }, []);

  const connect = useCallback(async () => {
    if (!WS_URL || !mountedRef.current || connectingRef.current) return;
    connectingRef.current = true;

    try {
      const token = await authApi.getWsToken();
      if (!mountedRef.current) {
        connectingRef.current = false;
        return;
      }

      const ws = new WebSocket(`${WS_URL}?token=${token}`);

      ws.onopen = () => {
        connectingRef.current = false;
        if (!mountedRef.current) { ws.close(); return; }
        const now = Date.now();
        console.debug('[WS] Connected');
        eventDebug.recordConnection(true, ws.readyState);
        setIsConnected(true);
        connectedSinceRef.current = now;
        setDiagnostics((prev) => ({ ...prev, connectedSince: now, reconnectAttempts: 0 }));
        wsRef.current = ws;
        reconnectAttemptsRef.current = 0;
        lastMessageTimeRef.current = now;

        // Filters live on the connection row; a fresh connection starts empty.
        // Reset the guard and resend the full current union as one `set`.
        lastSentRef.current = null;
        flushSet();

        // Start keepalive ping interval
        clearInterval(pingIntervalRef.current);
        clearTimeout(pongTimeoutRef.current);
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState !== WebSocket.OPEN) return;
          // Skip ping if a message was received recently
          if (Date.now() - lastMessageTimeRef.current < PING_INTERVAL_MS - 5000) return;
          try {
            ws.send(JSON.stringify({ action: 'ping' }));
          } catch {
            disconnectReasonRef.current = 'send_error';
            ws.close();
            return;
          }
          pongTimeoutRef.current = setTimeout(() => {
            disconnectReasonRef.current = 'pong_timeout';
            ws.close();
          }, PONG_TIMEOUT_MS);
        }, PING_INTERVAL_MS);
      };

      ws.onmessage = (event) => {
        // Any incoming message proves connection is alive
        lastMessageTimeRef.current = Date.now();
        clearTimeout(pongTimeoutRef.current);

        let parsed: PlatformEvent;
        try {
          parsed = JSON.parse(event.data);
        } catch {
          return;
        }

        // Control-plane frames (subscribe/unsubscribe acks, pong, status,
        // errors) carry a top-level `type` or `error` discriminator; a
        // PlatformEvent uses `eventType` and never a top-level `type`. Don't
        // dispatch control frames as events. (Generic check future-proofs
        // against new reply types — pong/status were added server-side.)
        const control = parsed as unknown as { type?: string; error?: string };
        if (control.type || control.error) {
          if (import.meta.env.DEV) console.debug('[WS] control:', control);
          return;
        }

        // Dev-only hook for e2e tests to inject synthetic events
        const devWindow = window as Window & {
          __injectPlatformEvent?: (json: string) => void;
        };
        if (import.meta.env.DEV && !devWindow.__injectPlatformEvent) {
          devWindow.__injectPlatformEvent = (json: string) => {
            ws.onmessage?.(new MessageEvent('message', { data: json }));
          };
        }
        // Deduplicate events by eventId (server may send duplicates when
        // multiple overlapping subscriptions match the same event)
        if (parsed.eventId) {
          if (seenIdsRef.current.has(parsed.eventId)) return;
          seenIdsRef.current.add(parsed.eventId);
          if (seenIdsRef.current.size > SEEN_IDS_MAX) {
            const keep = [...seenIdsRef.current].slice(-SEEN_IDS_TRIM);
            seenIdsRef.current = new Set(keep);
          }
        }
        // Dispatch only to handlers whose filters match this event
        let matchedCount = 0;
        for (const sub of subscriptionsRef.current) {
          if (matchesFilters(parsed, sub.filters)) {
            matchedCount += 1;
            sub.handler(parsed);
          }
        }
        // An inbound run event that matches zero local subscriptions means the
        // server forwarded it but our filters reject it (e.g. subjectId vs
        // runId mismatch) — a key signal for the "no CoT events" bug.
        eventDebug.recordInbound(parsed, matchedCount);
      };

      ws.onclose = (e: CloseEvent) => {
        const reason = disconnectReasonRef.current;
        const uptime = connectedSinceRef.current
          ? Math.round((Date.now() - connectedSinceRef.current) / 1000)
          : 0;
        console.debug(
          `[WS] Disconnected — reason: ${reason}, code: ${e.code}, uptime: ${uptime}s`,
        );
        disconnectReasonRef.current = 'unknown';
        connectedSinceRef.current = null;
        clearInterval(pingIntervalRef.current);
        clearTimeout(pongTimeoutRef.current);
        if (wsRef.current === ws) wsRef.current = null;
        eventDebug.recordConnection(false, ws.readyState);
        // New connection starts with an empty server-side filter set, so don't
        // let the byte-identical guard suppress the post-reconnect resend.
        lastSentRef.current = null;
        setIsConnected(false);
        setDiagnostics((prev) => ({
          ...prev,
          connectedSince: null,
          disconnectCount: prev.disconnectCount + 1,
          lastDisconnectReason: reason,
          lastDisconnectTime: Date.now(),
        }));
        scheduleReconnect(connect);
      };

      ws.onerror = () => {
        console.debug('[WS] Error event');
        // onclose will fire after onerror, triggering reconnect
      };
    } catch {
      // Token fetch failed — retry with backoff
      console.debug('[WS] Token fetch failed');
      connectingRef.current = false;
      disconnectReasonRef.current = 'unknown';
      setDiagnostics((prev) => ({
        ...prev,
        lastDisconnectReason: 'token_failure',
        lastDisconnectTime: Date.now(),
      }));
      scheduleReconnect(connect);
    }
  }, [flushSet, scheduleReconnect]);

  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      connectingRef.current = false;
      clearTimeout(reconnectTimerRef.current);
      flushScheduledRef.current = false;
      clearInterval(pingIntervalRef.current);
      clearTimeout(pongTimeoutRef.current);
      wsRef.current?.close();
      wsRef.current = null;
      subscriptionsRef.current = [];
    };
  }, [connect]);

  // Detect sleep/wake, tab visibility, and network changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        // Connection is already known-dead — reconnect immediately
        clearTimeout(reconnectTimerRef.current);
        reconnectAttemptsRef.current = 0;
        connect();
      } else {
        // Skip probe if data was flowing recently — connection is demonstrably alive
        if (Date.now() - lastMessageTimeRef.current < VISIBILITY_RECENT_MS) return;
        // Connection looks open but may be half-open after sleep — probe it
        try {
          ws.send(JSON.stringify({ action: 'ping' }));
          clearTimeout(pongTimeoutRef.current);
          pongTimeoutRef.current = setTimeout(() => {
            disconnectReasonRef.current = 'visibility_probe_timeout';
            ws.close(); // triggers onclose → reconnect
          }, VISIBILITY_PROBE_TIMEOUT_MS);
        } catch {
          disconnectReasonRef.current = 'send_error';
          ws.close();
        }
      }
    };

    const handleOnline = () => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        clearTimeout(reconnectTimerRef.current);
        reconnectAttemptsRef.current = 0;
        connect();
      }
    };

    const handleOffline = () => {
      // Proactively close so we don't sit with a stale readyState
      disconnectReasonRef.current = 'network_offline';
      wsRef.current?.close();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [connect]);

  const subscribe: SubscribeFn = useCallback(
    (filters, handler) => {
      const id = nextIdRef.current++;
      const sub: Subscription = { id, filters, handler };
      subscriptionsRef.current.push(sub);
      // Recompute and send the coalesced filter set (debounced).
      scheduleSync();

      return () => {
        subscriptionsRef.current = subscriptionsRef.current.filter((s) => s.id !== id);
        // Unmount shrinks the union — resend so the server drops orphaned
        // filters (replace semantics; no explicit unsubscribe needed).
        scheduleSync();
      };
    },
    [scheduleSync],
  );

  return (
    <PlatformEventContext.Provider value={{ subscribe, isConnected, diagnostics }}>
      {children}
    </PlatformEventContext.Provider>
  );
}

export function usePlatformEvents(): PlatformEventContextValue {
  const context = useContext(PlatformEventContext);
  if (!context) {
    throw new Error('usePlatformEvents must be used within a PlatformEventProvider');
  }
  return context;
}
